import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Github, ArrowRight } from "lucide-react";
import LumiereIcon from "@/components/LumiereIcon";
import { useLanguage } from "@/i18n";

const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 px-4 overflow-hidden">
      {/* Animated background orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse-glow pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-secondary/15 rounded-full blur-3xl animate-pulse-glow pointer-events-none" style={{ animationDelay: '1.5s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left space-y-8 z-10">
            {/* Badge */}
            <div className="inline-flex fade-in-up">
              <Badge 
                variant="outline" 
                className="px-4 py-2 border-primary/30 bg-primary/5 text-primary backdrop-blur-sm"
              >
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                {t('hero.badge')}
              </Badge>
            </div>

            {/* Heading */}
            <div className="space-y-4 fade-in-up" style={{ animationDelay: '0.1s' }}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-display font-bold leading-tight">
                {t('hero.title')}{" "}
                <span className="text-gradient">
                  {t('hero.titleHighlight')}
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 leading-relaxed">
                {t('hero.description')}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start fade-in-up" style={{ animationDelay: '0.2s' }}>
              <Button variant="hero" size="xl" asChild className="group">
                <a
                  href="https://apps.apple.com/app/lumiere-openclaw-ai-agents/id6758534255"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.09997 22C7.78997 22.05 6.79997 20.68 5.95997 19.47C4.24997 17 2.93997 12.45 4.69997 9.39C5.56997 7.87 7.12997 6.91 8.81997 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" />
                  </svg>
                  {t('hero.ctaPrimary')}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>
              <Button variant="hero" size="xl" asChild className="group">
                <a
                  href="https://chromewebstore.google.com/detail/lumiere/ppnnimejlhphcjckocpiinjmpmdfhhmg"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.49 0 2.87.47 4.01 1.26L7.8 17.65A7.007 7.007 0 0 1 5 12c0-3.87 3.13-7 7-7zm0 14c-1.49 0-2.87-.47-4.01-1.26L16.2 6.35A7.007 7.007 0 0 1 19 12c0 3.87-3.13 7-7 7z"/>
                    <circle cx="12" cy="12" r="3.5"/>
                  </svg>
                  {t('hero.ctaChrome')}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>
              <Button variant="hero-outline" size="xl" asChild>
                <a
                  href="https://github.com/ajamaica/lumiere"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Github className="w-5 h-5" />
                  {t('hero.ctaSecondary')}
                </a>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 justify-center lg:justify-start text-sm text-muted-foreground fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span>{t('hero.trustBadge1')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">{t('hero.trustBadge2')}</span>
              </div>
            </div>
          </div>

          {/* Right - Phone Mockup */}
          <div className="relative flex justify-center lg:justify-end fade-in-right" style={{ animationDelay: '0.2s' }}>
            <div className="relative">
              {/* Glow effect behind phone */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-secondary/20 to-accent/30 blur-3xl scale-110 animate-pulse-glow" />
              
              {/* Phone frame */}
              <div className="relative w-[280px] sm:w-[320px] h-[580px] sm:h-[640px] bg-card rounded-[3rem] border-4 border-border/50 shadow-2xl overflow-hidden float-animation">
                {/* Phone notch */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-background rounded-b-2xl z-20" />
                
                {/* Phone screen content */}
                <div className="absolute inset-2 rounded-[2.5rem] bg-background overflow-hidden">
                  {/* App header */}
                  <div className="p-4 border-b border-border/50 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                      <LumiereIcon className="w-4 h-4 text-primary-foreground" />
                    </div>
                    <span className="font-semibold text-foreground">{t('hero.chatHeader')}</span>
                    <div className="ml-auto w-2 h-2 bg-green-500 rounded-full" />
                  </div>
                  
                  {/* Chat messages */}
                  <div className="p-4 space-y-4">
                    {/* User message */}
                    <div className="flex justify-end">
                      <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-2.5 max-w-[85%] text-sm">
                        {t('hero.chatUser')}
                      </div>
                    </div>
                    
                    {/* AI response */}
                    <div className="flex justify-start">
                      <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 max-w-[90%] text-sm text-foreground">
                        <p className="mb-2">{t('hero.chatAiIntro')}</p>
                        <ul className="space-y-1 text-muted-foreground">
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{t('hero.chatAiPoint1')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{t('hero.chatAiPoint2')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            <span>{t('hero.chatAiPoint3')}</span>
                          </li>
                        </ul>
                      </div>
                    </div>

                    {/* Command message */}
                    <div className="flex justify-start">
                      <div className="bg-accent/20 border border-accent/30 rounded-xl px-3 py-2 text-sm text-accent">
                        <code>{t('hero.chatCommand')}</code> {t('hero.chatCommandResponse')}
                      </div>
                    </div>
                  </div>
                  
                  {/* Input bar */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border/50 bg-background/80 backdrop-blur-sm">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-muted rounded-full px-4 py-2.5 text-sm text-muted-foreground">
                        {t('hero.chatPlaceholder')}
                      </div>
                      <button className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
